import type { HaulingCategoryRate, HaulingDiscount, HaulingSettings } from '../types';

export interface HaulingFeeInput {
  distance: number | null | undefined;
  categoryId: number | null | undefined;
  requestLocation: 'home' | 'shop' | null | undefined;
  shopServicePreference: 'drop_off' | 'pickup' | null | undefined;
  settings: HaulingSettings | null | undefined;
  categoryRates: HaulingCategoryRate[] | undefined;
  discounts: HaulingDiscount[] | undefined;
}

/** Shop-pickup hauling fee (miles already); null when hauling doesn't apply. Lifted verbatim from Step7Review's memo. */
export function computeHaulingFee(i: HaulingFeeInput): number | null {
  const distance = i.distance ?? 0;
  if (!i.settings || !i.settings.enabled) return null;
  if (i.requestLocation === 'home') return null;
  if (i.requestLocation === 'shop' && i.shopServicePreference === 'drop_off') return null;
  if (!i.categoryId) return null;
  const rate = i.categoryRates?.find((r) => r.category_id === i.categoryId);
  if (!rate) return null;
  const chargeable = Math.max(0, distance - (i.settings.minimum_distance_miles ?? 0));
  let cost = (i.settings.base_fee ?? 0) + chargeable * rate.per_mile_rate;
  const now = new Date();
  for (const d of i.discounts ?? []) {
    if (!d.active) continue;
    if (d.start_date && new Date(d.start_date) > now) continue;
    if (d.end_date && new Date(d.end_date) < now) continue;
    if (d.min_distance && distance < d.min_distance) continue;
    if (d.category_ids && d.category_ids.length > 0 && !d.category_ids.includes(i.categoryId)) continue;
    cost = d.discount_type === 'percentage' ? cost * (1 - d.amount / 100) : cost - d.amount;
  }
  return Math.max(0, parseFloat(cost.toFixed(2)));
}

/** Flat home-service travel fee, mirroring calculate_travel_cost in the DB; null when nothing is charged. */
export function computeTravelFee(distance: number | null | undefined, settings: HaulingSettings | null | undefined): number | null {
  const miles = distance ?? 0;
  if (!settings?.travel_enabled || miles <= 0) return null;
  // Number(): PostgREST hands numeric columns back as strings on some paths.
  const free = Number(settings.travel_free_miles ?? 0);
  if (miles <= free) return null;
  const cost = Number(settings.travel_base_fee ?? 0) + (miles - free) * Number(settings.travel_per_mile_rate ?? 0);
  const fee = Math.max(0, parseFloat(cost.toFixed(2)));
  return fee > 0 ? fee : null;
}

export type TransportFee = { kind: 'hauling' | 'travel'; fee: number };

/**
 * What the requests trigger (auto_calculate_transport_fees) will charge: the
 * category hauling fee for shop pickup, the flat travel fee for home service,
 * nothing for drop-off. null when no transport charge applies.
 */
export function computeTransportFee(i: HaulingFeeInput): TransportFee | null {
  if (i.requestLocation === 'home') {
    const fee = computeTravelFee(i.distance, i.settings);
    return fee === null ? null : { kind: 'travel', fee };
  }
  const fee = computeHaulingFee(i);
  return fee === null ? null : { kind: 'hauling', fee };
}
